// The customer portal's sign-in gate (mounted at /portal by app/router.tsx).
// J6 (contract 1.4.0 §19–§20, F-J6-6): a deep link such as /portal/tickets/<id> (the Denwen tray's
// ticket link) survives sign-in. Signed out, the path goes to the sign-in page as router state (the
// password sign-in returns to it) and waits in sessionStorage (single sign-on leaves the page and
// comes back to /portal, where it is picked up once). Only a same-origin path under /portal is ever
// followed (lib/return-path.ts).
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/app/auth';
import { LoadingBlock } from '@/components/ui';
import { forgetPortalReturn, peekPortalReturn, rememberPortalReturn, safeReturnPath } from '@/lib/return-path';

export function RequirePortal() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingBlock className="h-full" />;
  const here = `${location.pathname}${location.search}${location.hash}`;
  if (!user) {
    const from = safeReturnPath(here, '/portal');
    if (from && from !== '/portal') rememberPortalReturn(from);
    return <Navigate to="/portal/login" replace state={from && from !== '/portal' ? { from } : undefined} />;
  }
  if (user.kind !== 'portal') return <Navigate to="/" replace />;
  // Back from single sign-on (which lands on /portal): the page the user first asked for.
  if (location.pathname === '/portal') {
    const pending = peekPortalReturn();
    if (pending && pending !== here) return <ReturnTo path={pending} />;
  }
  return <Outlet />;
}

/** Goes to the remembered path once (forgotten first, in an effect: render stays pure). */
function ReturnTo({ path }: { path: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    forgetPortalReturn();
    void navigate(path, { replace: true });
  }, [navigate, path]);
  return <LoadingBlock className="h-full" />;
}
